import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import {
  NovaApiService,
  NovaChatPayload,
  ConversationItem,
  GetConversationsResponse,
  ResponseMetadata,
  MetadataTableItem,
  FilterApplied,
  SqlQueryApplied,
  ConversationContextFilter
} from './api-services/nova-api.service';
import { NotificationService } from './notification.service';
import { FilterService } from './filter.service';
import { SharedDataService } from './shared-data.service';
import { transformTestToFilters, convertToFilterCondition, normalizeFilterRequestsForBackend } from '../shared/appUtil';
import { InitialFilterData } from '../shared/constant';
import * as Types from '../shared/types';
import dayjs from 'dayjs';

/** Conversation-specific context (filters, daterange, timezone) for display and send. */
export interface NovaConversationContext {
  start_date: string;
  end_date: string;
  timeZone: string;
  filters: Types.FilterRequest[];
}

// Interfaces
/** Optional override for chat context when sending a message (same shape as NovaChatPayload.custom_filters). */
export interface ChatMessageCustomFilters {
  start_date: string;
  end_date: string;
  timeZone: string;
  filters: Types.FilterRequest[];
}

export interface ChatMessage {
  id: string;
  text: string;
  isUser: boolean;
  timestamp: Date;
  attachments?: FileAttachment[];
  conversationId?: string; // Track which conversation this message belongs to
  /** Message id used by download-section-data (context_id). Decoupled from `id`, which is used for trackBy/menu keys. */
  contextId?: string;
  /** Override context for this message (date range, timezone, filters). When set, used instead of getNovaChatContext(). */
  customFilters?: ChatMessageCustomFilters;
  /** Tables from response metadata (system messages only) */
  tables?: MetadataTableItem[];
  /** Filters applied (system messages only) */
  filtersApplied?: FilterApplied[];
  /** SQL queries applied (system messages only) */
  sqlQueriesApplied?: SqlQueryApplied[];
}

export interface FileAttachment {
  id: string;
  name: string;
  size: number;
  type: string;
  url: string;
  response_type?: string; // "chart" | "file" | null - from API
  /** Chart description from API (metadata.charts[].chart_description) - markdown */
  chartDescription?: string;
  /** Chart thumbnail URL from API (metadata.charts[].thumbnail_url) - preferred for display */
  thumbnail_url?: string;
  /** Section step id (metadata.charts[].step) - used by download-section-data */
  step?: string;
}

export interface Conversation {
  id: string;
  title: string;
  messages: ChatMessage[];
  createdAt: Date;
  updatedAt: Date;
  timestamp: string; // UTC timestamp from API
}

@Injectable({
  providedIn: 'root'
})
export class NovaServiceService {
  // State management
  private _messageText = new BehaviorSubject<string>('');
  private _messages = new BehaviorSubject<ChatMessage[]>([]);
  private _isTyping = new BehaviorSubject<boolean>(false);
  private _currentConversation = new BehaviorSubject<Conversation | null>(null);
  private _conversations = new BehaviorSubject<Conversation[]>([]);
  private _attachments = new BehaviorSubject<FileAttachment[]>([]);
  private _showDeleteConfirmation = new BehaviorSubject<boolean>(false);
  private _conversationToDelete = new BehaviorSubject<string | null>(null);
  private _isHistoryOpen = new BehaviorSubject<boolean>(false);
  // Track multiple conversations being polled simultaneously
  private _pollingConversationIds = new Set<string>();
  private _pollingConversationIdsSubject = new BehaviorSubject<Set<string>>(new Set());
  // Track the latest processing step (current_step) per conversation from async-chat-status polling
  private _conversationStepMap = new Map<string, string>();
  private _conversationStepSubject = new BehaviorSubject<Map<string, string>>(new Map());

  /** Nova-specific date range for chat context (independent from insights dashboard). */
  private _novaDateRange: { start_date: string; end_date: string } | null = null;

  /** Per-conversation context (filters, daterange, timezone) from API or last applied. New conversations use default. */
  private _conversationContextMap = new Map<string, NovaConversationContext>();

  /** Emits when conversation context (date range, timezone, filters) is applied to UI so components can re-sync. */
  private _novaContextApplied$ = new BehaviorSubject<void>(undefined);

  // Observables
  public messageText$ = this._messageText.asObservable();
  public messages$ = this._messages.asObservable();
  public isTyping$ = this._isTyping.asObservable();
  public currentConversation$ = this._currentConversation.asObservable();
  public conversations$ = this._conversations.asObservable();
  public attachments$ = this._attachments.asObservable();
  public showDeleteConfirmation$ = this._showDeleteConfirmation.asObservable();
  public conversationToDelete$ = this._conversationToDelete.asObservable();
  public isHistoryOpen$ = this._isHistoryOpen.asObservable();
  public pollingConversationIds$ = this._pollingConversationIdsSubject.asObservable();
  public conversationSteps$ = this._conversationStepSubject.asObservable();

  // Getters
  get messageText(): string {
    return this._messageText.value;
  }

  get messages(): ChatMessage[] {
    return this._messages.value;
  }

  get isTyping(): boolean {
    return this._isTyping.value;
  }

  get currentConversation(): Conversation | null {
    return this._currentConversation.value;
  }

  get conversations(): Conversation[] {
    return this._conversations.value;
  }

  get attachments(): FileAttachment[] {
    return this._attachments.value;
  }

  get showDeleteConfirmation(): boolean {
    return this._showDeleteConfirmation.value;
  }

  get conversationToDelete(): string | null {
    return this._conversationToDelete.value;
  }

  get pollingConversationIds(): Set<string> {
    return new Set(this._pollingConversationIds);
  }

  get isHistoryOpen(): boolean {
    return this._isHistoryOpen.value;
  }

  /** Set date range for Nova chat context (from daterange picker in NOVA). */
  setNovaDateRange(start_date: string, end_date: string): void {
    this._novaDateRange = { start_date, end_date };
  }

  /** Default context for new conversations (no date range, EST, no filters). */
  private getDefaultContext(): NovaConversationContext {
    return {
      start_date: '',
      end_date: '',
      timeZone: 'EST',
      filters: []
    };
  }

  /** Convert API conversation context filters to FilterRequest[]. */
  private apiFiltersToFilterRequests(apiFilters: ConversationContextFilter[] | undefined): Types.FilterRequest[] {
    if (!apiFilters?.length) return [];
    const mapped = apiFilters.map((f) => ({
      logicalOperator: 'AND',
      columnName: f.columnName,
      operationType: f.operationType,
      value: f.value,
      isClusterFilter: false,
      clusterFilters: [],
    }));
    return normalizeFilterRequestsForBackend(mapped);
  }

  /** Save conversation-specific context (from API or from current UI when switching). */
  saveConversationContext(conversationId: string, context: NovaConversationContext): void {
    if (!conversationId?.trim()) return;
    this._conversationContextMap.set(conversationId, { ...context });
  }

  /** Get stored context for a conversation, or null if none. */
  getConversationContext(conversationId: string): NovaConversationContext | null {
    if (!conversationId?.trim()) return null;
    const ctx = this._conversationContextMap.get(conversationId);
    return ctx ? { ...ctx } : null;
  }

  /** Apply conversation context to UI (filter service, daterange, timezone). Used when switching conversation. */
  private applyConversationContextToUI(context: NovaConversationContext): void {
    this._novaDateRange = { start_date: context.start_date, end_date: context.end_date };
    this.sharedDataService.setSelectedTimezoneValue(context.timeZone || 'EST');
    if (context.filters?.length) {
      const conditions = convertToFilterCondition(context.filters);
      this.filterService.updateFilterData(conditions);
    } else {
      this.filterService.updateFilterData([{ ...InitialFilterData } as Types.FilterCondition]);
    }
    this._novaContextApplied$.next();
  }

  /** Observable that emits when conversation context was applied to UI (date range, timezone, filters). */
  get novaContextApplied$(): BehaviorSubject<void> {
    return this._novaContextApplied$;
  }

  /** Build chat context payload: start_date, end_date, filters, timeZone (from current UI state). */
  getNovaChatContext(): { start_date: string; end_date: string; filters: Types.FilterRequest[]; timeZone: string } {
    const start_date = this._novaDateRange?.start_date ?? '';
    const end_date = this._novaDateRange?.end_date ?? '';
    const filterDataArray = this.filterService.getFilterConditionDataValue();
    const { data: filters } = transformTestToFilters(filterDataArray);
    const timeZone = this.sharedDataService.selectedTimezoneValue || 'EST';
    return { start_date, end_date, filters: filters ?? [], timeZone };
  }

  // Check if current conversation is being polled
  isCurrentConversationPolling(): boolean {
    const currentConvId = this.currentConversation?.id;
    if (!currentConvId) return false;
    return this._pollingConversationIds.has(currentConvId);
  }

  // Add conversation to polling set
  private addPollingConversation(conversationId: string): void {
    if (!conversationId) return;
    this._pollingConversationIds.add(conversationId);
    this._pollingConversationIdsSubject.next(new Set(this._pollingConversationIds));
  }

  // Remove conversation from polling set
  private removePollingConversation(conversationId: string): void {
    if (!conversationId) return;
    this._pollingConversationIds.delete(conversationId);
    this._pollingConversationIdsSubject.next(new Set(this._pollingConversationIds));
    // Clear any tracked processing step once polling ends for this conversation
    this.clearConversationStep(conversationId);
  }

  // Current processing step (current_step) for the active conversation, or '' if none
  getCurrentConversationStep(): string {
    const currentConvId = this.currentConversation?.id;
    if (!currentConvId) return '';
    return this._conversationStepMap.get(currentConvId) ?? '';
  }

  // Store the latest processing step for a conversation and notify subscribers
  private setConversationStep(conversationId: string, step: string): void {
    if (!conversationId || !step) return;
    if (this._conversationStepMap.get(conversationId) === step) return;
    this._conversationStepMap.set(conversationId, step);
    this._conversationStepSubject.next(new Map(this._conversationStepMap));
  }

  // Remove the tracked step for a conversation and notify subscribers
  private clearConversationStep(conversationId: string): void {
    if (!conversationId) return;
    if (!this._conversationStepMap.has(conversationId)) return;
    this._conversationStepMap.delete(conversationId);
    this._conversationStepSubject.next(new Map(this._conversationStepMap));
  }

  constructor(
    private novaApiService: NovaApiService,
    private notificationService: NotificationService,
    private filterService: FilterService,
    private sharedDataService: SharedDataService
  ) {
    this.loadConversations();
    this.initializeNewConversation();
  }

  // Message text management
  setMessageText(text: string): void {
    this._messageText.next(text);
  }

  // Helper method to sort messages consistently
  private sortMessages(messages: ChatMessage[]): ChatMessage[] {
    // Sort messages by timestamp in ascending order (oldest first)
    // When timestamps are equal, user messages come before system messages
    return [...messages].sort((a, b) => {
      const timeA = a.timestamp instanceof Date ? a.timestamp.getTime() : new Date(a.timestamp).getTime();
      const timeB = b.timestamp instanceof Date ? b.timestamp.getTime() : new Date(b.timestamp).getTime();

      // First sort by timestamp
      if (timeA !== timeB) {
        return timeA - timeB;
      }

      // If timestamps are equal, user messages come before system messages
      if (a.isUser && !b.isUser) return -1;
      if (!a.isUser && b.isUser) return 1;
      return 0;
    });
  }

  // Messages management
  addMessage(message: ChatMessage): void {
    // Verify conversation_id matches current conversation before adding
    const currentConvId = this.currentConversation?.id;
    const messageConvId = message.conversationId;

    // If message has conversation_id, verify it matches current conversation
    if (messageConvId && currentConvId && messageConvId !== currentConvId) {
      // console.warn(`Message conversation_id (${messageConvId}) does not match current conversation (${currentConvId}). Ignoring message.`);
      return;
    }

    // If message doesn't have conversation_id but we have a current conversation, add it
    if (!messageConvId && currentConvId) {
      message.conversationId = currentConvId;
    }

    // Only add message if conversation_id matches or if we're in a new conversation (no ID yet)
    if (!messageConvId || !currentConvId || messageConvId === currentConvId) {
      const currentMessages = this._messages.value;
      const updatedMessages = [...currentMessages, message];
      const sortedMessages = this.sortMessages(updatedMessages);

      this._messages.next(sortedMessages);
      this.updateCurrentConversation();
    }
  }

  clearMessages(): void {
    this._messages.next([]);
  }

  // Typing state
  setTyping(isTyping: boolean): void {
    this._isTyping.next(isTyping);
  }

  // Attachments management
  setAttachments(attachments: FileAttachment[]): void {
    this._attachments.next(attachments);
  }

  addAttachment(attachment: FileAttachment): void {
    const currentAttachments = this._attachments.value;
    this._attachments.next([...currentAttachments, attachment]);
  }

  removeAttachment(attachmentId: string): void {
    const currentAttachments = this._attachments.value;
    const updatedAttachments = currentAttachments.filter(att => att.id !== attachmentId);
    this._attachments.next(updatedAttachments);
  }

  clearAttachments(): void {
    this._attachments.next([]);
  }

  // Conversation management
  loadConversations(): void {
    // Load conversations from Nova API
    this.novaApiService.getConversations().subscribe({
      next: (response: GetConversationsResponse) => {
        // Support response.success or truthy response when API shape varies
        if (response?.success === false) return;
        // Support both camelCase and PascalCase; data may be under response.data or at top level
        const res = response as unknown as Record<string, unknown>;
        const data = res['data'] as Record<string, unknown> | undefined;
        const raw = data ?? res;
        const rawList = (raw['conversations'] ?? raw['Conversations']) as Array<Record<string, unknown>> | undefined;
        const list = Array.isArray(rawList) ? rawList : [];
        const conversations = list.map((item: Record<string, unknown>) => {
          const id = (item['conversation_id'] ?? item['ConversationId'] ?? item['id']) as string;
          const title = (item['conversation_title'] ?? item['ConversationTitle'] ?? item['title']) as string;
          const ts = (item['timestamp'] ?? item['Timestamp'] ?? item['last_updated'] ?? item['LastUpdated']) as string | undefined;
          const lastUpdated = (item['last_updated'] ?? item['LastUpdated'] ?? ts) as string | undefined;
          const dateStr = lastUpdated || ts || new Date().toISOString();
          return {
            id: id || '',
            title: typeof title === 'string' ? title : '...',
            messages: [],
            createdAt: new Date(ts || dateStr),
            updatedAt: new Date(dateStr),
            timestamp: dateStr
          };
        });
        // Don't replace the list with empty when we already have conversations (e.g. during send/polling)
        const current = this._conversations.value;
        if (conversations.length > 0 || current.length === 0) {
          this._conversations.next(conversations);
          this.saveConversations();
        }
      },
      error: (error) => {
        console.error('Error loading conversations from API:', error);
        // Fallback to localStorage
        this.loadConversationsFromLocalStorage();
      }
    });
  }

  // Method to refresh conversations (useful when Nova is opened)
  refreshConversations(): void {
    this.loadConversations();
  }

  private loadConversationsFromLocalStorage(): void {
    const saved = sessionStorage.getItem('nova-conversations');
    if (saved) {
      try {
        const conversations = JSON.parse(saved).map((conv: any) => {
          const messages = conv.messages.map((msg: any) => ({
            ...msg,
            timestamp: new Date(msg.timestamp)
          }));
          // Sort messages when loading from localStorage to ensure consistent order
          const sortedMessages = messages.length > 0 ? this.sortMessages(messages) : [];
          return {
            ...conv,
            createdAt: new Date(conv.createdAt),
            updatedAt: new Date(conv.updatedAt),
            timestamp: conv.timestamp || conv.createdAt?.toISOString() || new Date().toISOString(),
            messages: sortedMessages
          };
        });
        this._conversations.next(conversations);
      } catch (error) {
        console.error('Error loading conversations from localStorage:', error);
        this._conversations.next([]);
      }
    }
  }

  saveConversations(): void {
    sessionStorage.setItem('nova-conversations', JSON.stringify(this._conversations.value));
  }

  initializeNewConversation(): void {
    const newConversation: Conversation = {
      id: '', // No ID for new conversations - will be set when API responds
      title: 'New Conversation',
      messages: [],
      createdAt: new Date(),
      updatedAt: new Date(),
      timestamp: new Date().toISOString()
    };
    this._currentConversation.next(newConversation);
    this._messages.next([]);
  }

  startNewConversation(): void {
    // Save current conversation's context to map before clearing (so switching back shows it)
    const currentId = this._currentConversation.value?.id;
    if (currentId?.trim()) {
      const currentContext = this.getNovaChatContext();
      this.saveConversationContext(currentId, {
        start_date: currentContext.start_date,
        end_date: currentContext.end_date,
        timeZone: currentContext.timeZone,
        filters: currentContext.filters ?? []
      });
    }

    // Save current conversation if it has messages
    if (this._currentConversation.value && this._messages.value.length > 0) {
      this.saveCurrentConversation();
    }

    // Initialize new conversation locally and apply default context (filters, daterange, timezone)
    this.initializeNewConversation();
    this.applyConversationContextToUI(this.getDefaultContext());
  }

  private saveCurrentConversation(): void {
    const currentConv = this._currentConversation.value;
    if (currentConv && this._messages.value.length > 0) {
      // Check if messages have actually changed by comparing message count and IDs
      const messagesChanged = this.haveMessagesChanged(currentConv.messages, this._messages.value);

      // Only update updatedAt if messages have actually changed
      const updatedConv = {
        ...currentConv,
        messages: this._messages.value,
        updatedAt: messagesChanged ? new Date() : currentConv.updatedAt
      };

      const conversations = this._conversations.value;
      const existingIndex = conversations.findIndex(c => c.id === currentConv.id);

      if (existingIndex >= 0) {
        conversations[existingIndex] = updatedConv;
      } else {
        conversations.unshift(updatedConv);
      }

      this._conversations.next(conversations);
      this.saveConversations();
    }
  }

  // Helper method to check if messages have changed
  // Note: User messages get new IDs each time they're loaded, so we compare by content
  private haveMessagesChanged(oldMessages: ChatMessage[], newMessages: ChatMessage[]): boolean {
    // If message counts are different, messages have changed
    if (oldMessages.length !== newMessages.length) {
      return true;
    }

    // Compare messages by content (text, timestamp, isUser) since IDs may change
    // Sort both arrays by timestamp for consistent comparison
    const sortedOld = [...oldMessages].sort((a, b) => {
      const timeA = a.timestamp instanceof Date ? a.timestamp.getTime() : new Date(a.timestamp).getTime();
      const timeB = b.timestamp instanceof Date ? b.timestamp.getTime() : new Date(b.timestamp).getTime();
      return timeA - timeB;
    });

    const sortedNew = [...newMessages].sort((a, b) => {
      const timeA = a.timestamp instanceof Date ? a.timestamp.getTime() : new Date(a.timestamp).getTime();
      const timeB = b.timestamp instanceof Date ? b.timestamp.getTime() : new Date(b.timestamp).getTime();
      return timeA - timeB;
    });

    // Compare each message by content (not ID, since user message IDs are regenerated)
    for (let i = 0; i < sortedOld.length; i++) {
      const oldMsg = sortedOld[i];
      const newMsg = sortedNew[i];

      // Compare by text, timestamp, and isUser flag
      if (oldMsg.text !== newMsg.text ||
          oldMsg.isUser !== newMsg.isUser ||
          (oldMsg.timestamp instanceof Date ? oldMsg.timestamp.getTime() : new Date(oldMsg.timestamp).getTime()) !==
          (newMsg.timestamp instanceof Date ? newMsg.timestamp.getTime() : new Date(newMsg.timestamp).getTime())) {
        return true;
      }
    }

    return false;
  }

  selectConversation(conversationId: string): void {
    // Don't switch if we're already on this conversation
    if (this._currentConversation.value?.id === conversationId) {
      return;
    }

    const conversation = this._conversations.value.find(c => c.id === conversationId);
    if (conversation) {
      // Save current conversation's context (filters, daterange, timezone) to map before switching
      const previousId = this._currentConversation.value?.id;
      if (previousId?.trim()) {
        const currentContext = this.getNovaChatContext();
        this.saveConversationContext(previousId, {
          start_date: currentContext.start_date,
          end_date: currentContext.end_date,
          timeZone: currentContext.timeZone,
          filters: currentContext.filters ?? []
        });
      }

      // Save current conversation if it has messages before switching
      if (this._currentConversation.value && this._messages.value.length > 0) {
        this.saveCurrentConversation();
      }

      // Clear typing state when switching conversations (typing indicator uses isCurrentConversationPolling which checks conversation_id)
      // Do NOT cancel polling - let it continue in background for other conversations
      // Only clear typing if the previous conversation was being polled
      if (this._currentConversation.value?.id && this._pollingConversationIds.has(this._currentConversation.value.id)) {
        this.setTyping(false);
      }

      // If the new conversation is already being polled, show typing indicator
      if (conversationId && this._pollingConversationIds.has(conversationId)) {
        this.setTyping(true);
      }

      // Clear message text and attachments when switching conversations
      this.setMessageText('');
      this.clearAttachments();

      // Clear messages first to show loading state
      this._messages.next([]);

      // Set current conversation
      this._currentConversation.next(conversation);
      // Sort messages from conversation before setting them to maintain consistent order
      const sortedConversationMessages = conversation.messages.length > 0
        ? this.sortMessages(conversation.messages)
        : [];
      this._messages.next(sortedConversationMessages);

      // Apply conversation-specific context (filters, daterange, timezone) for this conversation
      const contextToApply = this.getConversationContext(conversationId) ?? this.getDefaultContext();
      this.applyConversationContextToUI(contextToApply);

      // Fetch conversation-specific filters (date range, timezone, filters) when conversation changes
      this.novaApiService.getConversationCustomFilters({ conversation_id: conversationId }).subscribe({
        next: (res) => {
          if (this._currentConversation.value?.id !== conversationId) return;
          if (!res.success || !res.data?.active_custom_filters) return;
          const active = res.data.active_custom_filters;
          const defaultCtx = this.getDefaultContext();
          const apiContext: NovaConversationContext = {
            start_date: active.start_date ?? defaultCtx.start_date,
            end_date: active.end_date ?? defaultCtx.end_date,
            timeZone: active.timeZone ?? defaultCtx.timeZone,
            filters: this.apiFiltersToFilterRequests(active.filters) ?? []
          };
          this.saveConversationContext(conversationId, apiContext);
          if (this._currentConversation.value?.id === conversationId) {
            this.applyConversationContextToUI(apiContext);
          }
        }
      });

      // Load conversation messages using the new API
      this.novaApiService.getConversationMessages({ conversation_id: conversationId }).subscribe({
        next: (response) => {
          // Verify we're still on the same conversation (user might have switched again)
          if (this._currentConversation.value?.id !== conversationId) {
            return;
          }

          if (response.success && response.data.messages) {
            // Store conversation-specific context: active_custom_filters when present, else flat meta, else default (filters [], no date range, EST)
            const meta = response.data.conversation_metadata;
            const active = meta?.active_custom_filters;
            const hasActive = active && (active.filters?.length || active.start_date || active.end_date || active.timeZone);
            const hasFlat = meta && !hasActive && (meta.filters?.length || meta.start_date || meta.end_date || meta.timeZone);
            const defaultCtx = this.getDefaultContext();
            const apiContext: NovaConversationContext = hasActive
              ? {
                  start_date: active!.start_date ?? defaultCtx.start_date,
                  end_date: active!.end_date ?? defaultCtx.end_date,
                  timeZone: active!.timeZone ?? defaultCtx.timeZone,
                  filters: this.apiFiltersToFilterRequests(active!.filters) ?? []
                }
              : hasFlat
                ? {
                    start_date: meta!.start_date ?? defaultCtx.start_date,
                    end_date: meta!.end_date ?? defaultCtx.end_date,
                    timeZone: meta!.timeZone ?? defaultCtx.timeZone,
                    filters: this.apiFiltersToFilterRequests(meta!.filters) ?? []
                  }
                : { ...defaultCtx, filters: [] };
            this.saveConversationContext(conversationId, apiContext);
            if (this._currentConversation.value?.id === conversationId) {
              this.applyConversationContextToUI(apiContext);
            }

            // Convert API messages to ChatMessage format - create user-system pairs
            const allMessages: ChatMessage[] = [];

            response.data.messages.forEach(msg => {
              const msgTimestamp = new Date(msg.timestamp);
              const userTimestamp = msgTimestamp;
              const systemTimestamp = new Date(msgTimestamp.getTime() + 1); // System message 1ms after user

              // Add user message first (with earlier timestamp)
              if (msg.user_message && msg.user_message.trim()) {
                allMessages.push({
                  id: this.generateId(),
                  text: msg.user_message,
                  isUser: true,
                  timestamp: userTimestamp,
                  conversationId: conversationId // Track conversation_id for loaded messages
                });
              }

              // Add system message second (with later timestamp)
              if (msg.system_message && msg.system_message.trim()) {
                const systemMessage: ChatMessage = {
                  id: msg.context_id,
                  text: msg.system_message,
                  isUser: false,
                  timestamp: systemTimestamp,
                  conversationId: conversationId, // Track conversation_id for loaded messages
                  contextId: msg.context_id // Message id for download-section-data
                };

                // Build attachments from message.metadata.files and message.metadata.charts
                const metaAttachments = this.attachmentsFromMetadata(msg.metadata);
                if (metaAttachments.length > 0) {
                  systemMessage.attachments = metaAttachments;
                }
                // Tables, filters, and SQL from metadata for UI display
                if (msg.metadata?.tables?.length) {
                  systemMessage.tables = msg.metadata.tables;
                }
                if (msg.metadata?.filters_applied?.length) {
                  systemMessage.filtersApplied = msg.metadata.filters_applied;
                }
                if (msg.sql_queries_applied?.length) {
                  systemMessage.sqlQueriesApplied = msg.sql_queries_applied;
                }

                allMessages.push(systemMessage);
              }
            });

            // Sort all messages by timestamp in ascending order (oldest first)
            // When timestamps are equal, user messages come before system messages
            const sortedMessages = this.sortMessages(allMessages);

            // Only update if we're still on this conversation
            if (this._currentConversation.value?.id === conversationId) {
              this._messages.next(sortedMessages);
              // Preserve original updatedAt to maintain conversation order in list
              // Only update messages, don't change updatedAt when just loading/selecting
              const updatedConv = { ...conversation, messages: sortedMessages };
              this._currentConversation.next(updatedConv);

              // Only update in conversations list if messages have actually changed
              // This prevents unnecessary re-renders and re-sorts
              const conversations = this._conversations.value;
              const conversationIndex = conversations.findIndex(c => c.id === conversationId);
              if (conversationIndex >= 0) {
                const existingConv = conversations[conversationIndex];
                const messagesChanged = this.haveMessagesChanged(existingConv.messages, sortedMessages);

                // Only update if messages changed to avoid unnecessary re-renders
                if (messagesChanged) {
                  conversations[conversationIndex] = updatedConv;
                  this._conversations.next([...conversations]); // Create new array to trigger change detection
                }
              }
            }
          }
        },
        error: (error) => {
          console.error('Error loading conversation messages:', error);
          // Continue with local messages if API fails
          // But only if we're still on this conversation
          if (this._currentConversation.value?.id === conversationId && conversation.messages.length > 0) {
            // Sort messages before setting them to maintain consistent order
            const sortedMessages = this.sortMessages(conversation.messages);
            this._messages.next(sortedMessages);
          }
        }
      });
    }
  }

  updateCurrentConversation(): void {
    const currentConv = this._currentConversation.value;
    if (currentConv) {
      const updatedConv = {
        ...currentConv,
        messages: this._messages.value,
        updatedAt: new Date()
      };
      this._currentConversation.next(updatedConv);
    }
  }

  private isRenamingConversation = false;

  /**
   * Update conversation title locally without calling the API
   * Used when receiving title updates from async-chat-status
   */
  private updateConversationTitleLocally(conversationId: string, newTitle: string): void {
    const conversations = this._conversations.value;
    const conversation = conversations.find(c => c.id === conversationId);
    if (conversation && conversation.title !== newTitle) {
      // Preserve original updatedAt to prevent conversation from moving to different date group
      const originalUpdatedAt = conversation.updatedAt;
      conversation.title = newTitle;
      conversation.updatedAt = originalUpdatedAt;
      this._conversations.next([...conversations]);
      this.saveConversations();

      // Also update current conversation if it matches
      if (this.currentConversation?.id === conversationId) {
        this._currentConversation.next({
          ...this.currentConversation,
          title: newTitle
        });
      }
    }
  }

  saveConversationTitle(conversationId: string, newTitle: string): void {
    // Prevent multiple simultaneous rename calls
    if (this.isRenamingConversation) {
      return;
    }

    const conversations = this._conversations.value;
    const conversation = conversations.find(c => c.id === conversationId);
    if (conversation) {
      // If newTitle is empty or just whitespace, use the original conversation title
      const trimmedTitle = newTitle?.trim() || '';
      const finalTitle = trimmedTitle || conversation.title;

      // If title hasn't changed, don't call the API
      if (conversation.title === finalTitle) {
        return;
      }

      this.isRenamingConversation = true;
      // Preserve original updatedAt to prevent conversation from moving to different date group
      const originalUpdatedAt = conversation.updatedAt;

      // Update title via API
      this.novaApiService.renameConversation(conversationId, finalTitle).subscribe({
        next: (response) => {
          this.isRenamingConversation = false;
          if (response.success) {
            // Show success notification
            this.notificationService.success('Success', 'Conversation Renamed Successfully');
            // Immediately update the title locally for instant UI feedback
            conversation.title = response.data.conversation_title;
            // Preserve original updatedAt to maintain conversation position in date groups
            conversation.updatedAt = originalUpdatedAt;
            this._conversations.next([...conversations]);
            this.saveConversations();

            // Call conversations API after successful rename to sync with server
            this.novaApiService.getConversations().subscribe({
              next: (apiResponse: GetConversationsResponse) => {
                if (apiResponse.success && apiResponse.data.conversations) {
                  // Get current conversations state (which includes the immediate title update)
                  const currentConversations = this._conversations.value;

                  // Create a map of API conversations for quick lookup
                  const apiConversationsMap = new Map<string, ConversationItem>();
                  apiResponse.data.conversations.forEach((item: ConversationItem) => {
                    apiConversationsMap.set(item.conversation_id, item);
                  });

                  // Preserve original sequence by merging API data into existing conversations
                  // while maintaining the original order based on updatedAt
                  const mergedConversations = currentConversations.map((existingConv) => {
                    const apiItem = apiConversationsMap.get(existingConv.id);
                    if (apiItem) {
                      // Update title from API, but preserve all other data including updatedAt
                      return {
                        ...existingConv,
                        title: apiItem.conversation_title,
                        // Preserve original updatedAt to maintain sequence and grouping
                        updatedAt: existingConv.updatedAt,
                        timestamp: existingConv.timestamp
                      };
                    }
                    return existingConv;
                  });

                  // Add any new conversations from API that don't exist locally
                  const currentConversationIds = new Set(currentConversations.map(c => c.id));
                  apiResponse.data.conversations.forEach((item: ConversationItem) => {
                    if (!currentConversationIds.has(item.conversation_id)) {
                      mergedConversations.push({
                        id: item.conversation_id,
                        title: item.conversation_title,
                        messages: [],
                        createdAt: new Date(item.timestamp),
                        updatedAt: new Date(item.last_updated || item.timestamp),
                        timestamp: item.last_updated || item.timestamp
                      });
                    }
                  });

                  // Ensure the renamed conversation maintains its original updatedAt
                  const renamedConv = mergedConversations.find(c => c.id === conversationId);
                  if (renamedConv) {
                    renamedConv.updatedAt = originalUpdatedAt;
                    renamedConv.timestamp = originalUpdatedAt.toISOString();
                  }

                  // Sort by updatedAt (newest first) to maintain proper sequence within groups
                  mergedConversations.sort((a, b) => {
                    const dateA = a.updatedAt ? new Date(a.updatedAt).getTime() : 0;
                    const dateB = b.updatedAt ? new Date(b.updatedAt).getTime() : 0;
                    return dateB - dateA;
                  });

                  this._conversations.next(mergedConversations);
                  this.saveConversations();
                }
              },
              error: (error) => {
                console.error('Error loading conversations from API after rename:', error);
              }
            });
          } else {
            console.error('Failed to rename conversation:', response.message);
            // Fallback to local update
            conversation.title = finalTitle;
            conversation.updatedAt = originalUpdatedAt;
            this._conversations.next([...conversations]);
            this.saveConversations();
          }
        },
        error: (error) => {
          this.isRenamingConversation = false;
          console.error('Error renaming conversation:', error);
          // Fallback to local update - preserve original updatedAt
          conversation.title = finalTitle;
          conversation.updatedAt = originalUpdatedAt;
          this._conversations.next([...conversations]);
          this.saveConversations();
        }
      });
    }
  }

  confirmDeleteConversation(conversationId: string): void {
    this._conversationToDelete.next(conversationId);
    this._showDeleteConfirmation.next(true);
  }

  confirmDelete(): void {
    const conversationId = this._conversationToDelete.value;
    if (conversationId) {
      // Delete conversation via API
      this.novaApiService.deleteConversation(conversationId).subscribe({
        next: (response) => {
          if (response.success) {
            const conversations = this._conversations.value.filter(c => c.id !== conversationId);
            this._conversations.next(conversations);
            this.saveConversations();

            // If we're deleting the current conversation, clear it but don't create a new one
            if (this._currentConversation.value?.id === conversationId) {
              this._currentConversation.next(null);
              this._messages.next([]);
            }
          } else {
            console.error('Failed to delete conversation:', response.message);
            // Fallback to local deletion
            const conversations = this._conversations.value.filter(c => c.id !== conversationId);
            this._conversations.next(conversations);
            this.saveConversations();

            if (this._currentConversation.value?.id === conversationId) {
              this._currentConversation.next(null);
              this._messages.next([]);
            }
          }
        },
        error: (error) => {
          console.error('Error deleting conversation:', error);
          // Fallback to local deletion
          const conversations = this._conversations.value.filter(c => c.id !== conversationId);
          this._conversations.next(conversations);
          this.saveConversations();

          if (this._currentConversation.value?.id === conversationId) {
            this._currentConversation.next(null);
            this._messages.next([]);
          }
        }
      });
    }
    this.cancelDelete();
  }

  cancelDelete(): void {
    this._showDeleteConfirmation.next(false);
    this._conversationToDelete.next(null);
  }

  // Utility methods
  generateId(): string {
    return Math.random().toString(36).substring(2, 11);
  }

  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  getCurrentDate(): string {
    return new Date().toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  }

  // Group messages by date for display
  getGroupedMessages(): Array<{date: string, messages: ChatMessage[]}> {
    const currentConvId = this.currentConversation?.id;
    // Filter messages to only include those matching the current conversation_id
    const messages = this._messages.value.filter(msg => {
      // If message has conversation_id, verify it matches current conversation
      if (msg.conversationId && currentConvId) {
        return msg.conversationId === currentConvId;
      }
      // If message doesn't have conversation_id but we have a current conversation, exclude it
      if (!msg.conversationId && currentConvId) {
        return false;
      }
      // If no current conversation, include messages without conversation_id (for new conversations)
      return !currentConvId;
    });

    if (messages.length === 0) return [];

    // Group messages by date
    const groupedByDate = new Map<string, ChatMessage[]>();

    messages.forEach(message => {
      const messageDate = new Date(message.timestamp);
      const dateKey = messageDate.toISOString().split('T')[0]; // YYYY-MM-DD format

      if (!groupedByDate.has(dateKey)) {
        groupedByDate.set(dateKey, []);
      }
      groupedByDate.get(dateKey)!.push(message);
    });

    // Convert to array and sort by date (oldest first)
    const groupedArray = Array.from(groupedByDate.entries())
      .map(([date, messages]) => ({
        date,
        messages: messages.sort((a, b) => {
          // Sort messages in ascending order (older first) within each date group
          // When timestamps are equal, user messages come before system messages
          const timeA = a.timestamp instanceof Date ? a.timestamp.getTime() : new Date(a.timestamp).getTime();
          const timeB = b.timestamp instanceof Date ? b.timestamp.getTime() : new Date(b.timestamp).getTime();

          // First sort by timestamp
          if (timeA !== timeB) {
            return timeA - timeB;
          }

          // If timestamps are equal, user messages come before system messages
          if (a.isUser && !b.isUser) return -1;
          if (!a.isUser && b.isUser) return 1;
          return 0;
        })
      }))
      .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

    return groupedArray;
  }

  // Format date for display using the same format as getCurrentDate
  formatDateForDisplay(dateString: string): string {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  }

  // Message sending logic
  sendMessage(): void {
    if (!this.messageText?.trim() && this.attachments.length === 0) return;

    // Get current conversation_id (may be empty for new conversations)
    const currentConvId = this.currentConversation?.id || '';

    const userMessage: ChatMessage = {
      id: this.generateId(),
      text: this.messageText,
      isUser: true,
      timestamp: new Date(),
      attachments: this.attachments.length > 0 ? [...this.attachments] : undefined,
      conversationId: currentConvId // Track which conversation this message belongs to
    };

    this.addMessage(userMessage);
    this.setMessageText('');
    this.clearAttachments();

    // Send message to Nova API
    this.sendMessageToNova(userMessage);
  }

  /**
   * Send a plain-text message without mutating the current draft input or attachments.
   * Used by canned reply actions that should post directly into the active conversation.
   */
  sendMessageWithText(text: string): void {
    const trimmedText = text?.trim();
    if (!trimmedText) return;

    const currentConvId = this.currentConversation?.id || '';
    const userMessage: ChatMessage = {
      id: this.generateId(),
      text: trimmedText,
      isUser: true,
      timestamp: new Date(),
      conversationId: currentConvId
    };

    this.addMessage(userMessage);
    this.sendMessageToNova(userMessage);
  }

  /**
   * Send a chat message using the payload format: { query, custom_filters: { start_date, end_date, timeZone, filters } }.
   * Uses custom_filters for the request context instead of the current Nova date range and filters.
   */
  sendMessageWithPayload(payload: NovaChatPayload): void {
    const currentConvId = this.currentConversation?.id || '';
    const userMessage: ChatMessage = {
      id: this.generateId(),
      text: payload.query,
      isUser: true,
      timestamp: new Date(),
      conversationId: currentConvId,
      customFilters: {
        start_date: payload.custom_filters.start_date,
        end_date: payload.custom_filters.end_date,
        timeZone: payload.custom_filters.timeZone,
        filters: normalizeFilterRequestsForBackend(
          (payload.custom_filters.filters ?? []) as Types.FilterRequest[]
        ),
      }
    };
    this.addMessage(userMessage);
    this.sendMessageToNova(userMessage);
  }

  private sendMessageToNova(userMessage: ChatMessage): void {
    this.setTyping(true);

    const rawContext = userMessage.customFilters ?? this.getNovaChatContext();
    const filtersForApi = normalizeFilterRequestsForBackend(rawContext.filters ?? []);
    const context = { ...rawContext, filters: filtersForApi };
    // Always pass in format: { query, custom_filters: { start_date, end_date, timeZone, filters } }
    const request: any = {
      query: userMessage.text,
      custom_filters: {
        start_date: context.start_date,
        end_date: context.end_date,
        timeZone: context.timeZone,
        filters: context.filters ?? []
      }
    };

    if (this.currentConversation?.id && this.currentConversation.id.trim() !== '') {
      request.conversation_id = this.currentConversation.id;
      // Store current context for this conversation (so it shows when switching back; API may overwrite when response returns)
      this.saveConversationContext(this.currentConversation.id, {
        start_date: context.start_date,
        end_date: context.end_date,
        timeZone: context.timeZone,
        filters: context.filters ?? []
      });
    }

    // Submit chat query
    this.novaApiService.submitChat(request).subscribe({
      next: (response) => {
        if (response.success && response.data.job_id) {
          // Update current conversation with the new conversation ID if it's a new conversation
          if ((!this.currentConversation?.id || this.currentConversation.id.trim() === '') && response.data.conversation_id) {
            const newConversationId = response.data.conversation_id;
            // Update the user message with the new conversation_id
            const currentMessages = this._messages.value;
            const updatedMessages = currentMessages.map(msg => {
              // Update the last user message (the one we just sent) with the new conversation_id
              if (msg.isUser && (!msg.conversationId || msg.conversationId === '')) {
                return { ...msg, conversationId: newConversationId };
              }
              return msg;
            });
            this._messages.next(updatedMessages);

            const newConversation: Conversation = {
              id: newConversationId,
              title: 'New Conversation',
              messages: updatedMessages,
              createdAt: new Date(),
              updatedAt: new Date(),
              timestamp: new Date().toISOString()
            };
            this._currentConversation.next(newConversation);
          } else if (this.currentConversation?.id && response.data.conversation_id) {
            // Verify the response conversation_id matches the current conversation
            if (this.currentConversation.id !== response.data.conversation_id) {
              // console.warn(`Response conversation_id (${response.data.conversation_id}) does not match current conversation (${this.currentConversation.id}).`);
              this.setTyping(false);
              return;
            }
          }

          // Start polling for the response (conversations list is refreshed when polling completes)
          this.pollChatStatus(response.data.job_id, response.data.conversation_id);
        } else {
          console.error('Failed to submit chat:', response);
          this.setTyping(false);
        }
      },
      error: (error) => {
        console.error('Error submitting chat to Nova:', error);
        this.setTyping(false);
      }
    });
  }

  private pollChatStatus(jobId: string, conversationId: string): void {
    const pollInterval = 5000; // Poll every 5 seconds
    const maxAttempts = 60; // Maximum 5 minutes of polling
    let attempts = 0;

    // Add this conversation to the polling set (supports multiple simultaneous polls)
    this.addPollingConversation(conversationId);

    // Set typing state only if this is the current conversation
    if (this.currentConversation?.id === conversationId) {
      this.setTyping(true);
    }

    const poll = () => {
      attempts++;

      this.novaApiService.checkChatStatus({ job_id: jobId, conversation_id: conversationId }).subscribe({
        next: (response) => {
          // Verify the conversation_id from the response matches the one we're polling for
          const responseConversationId = response.data?.conversation_id;
          if (responseConversationId && responseConversationId !== conversationId) {
            // console.warn(`Response conversation_id (${responseConversationId}) does not match polling conversation_id (${conversationId}). Ignoring response.`);
            // Continue polling in case this was a timing issue
            if (attempts < maxAttempts) {
              setTimeout(poll, pollInterval);
            }
            return;
          }

          // Update the processing step so the typing indicator reflects live progress
          if (response.data?.current_step) {
            this.setConversationStep(conversationId, response.data.current_step);
          }

          if (response.success && response.data.status === 'completed') {
            // Verify conversation_id one more time before processing
            const verifiedConversationId = response.data?.conversation_id || conversationId;
            if (verifiedConversationId !== conversationId) {
              //console.warn(`Completed response conversation_id (${verifiedConversationId}) does not match expected (${conversationId}). Ignoring.`);
              // Only clear typing if this was for the current conversation
              if (this.currentConversation?.id === conversationId) {
                this.setTyping(false);
              }
              // Remove from polling set
              this.removePollingConversation(conversationId);
              return;
            }

            // Ensure system message timestamp is after the last user message
            // Only check current messages if we're on the correct conversation
            const currentConvId = this.currentConversation?.id;
            const currentMessages = currentConvId === conversationId ? this._messages.value : [];
            // Find the user message with the highest timestamp (most recent)
            const userMessages = currentMessages.filter(m => m.isUser && m.conversationId === conversationId);
            let lastUserTimestamp = 0;
            if (userMessages.length > 0) {
              // Sort user messages by timestamp descending to get the most recent one
              const sortedUserMessages = [...userMessages].sort((a, b) => {
                const timeA = a.timestamp instanceof Date ? a.timestamp.getTime() : new Date(a.timestamp).getTime();
                const timeB = b.timestamp instanceof Date ? b.timestamp.getTime() : new Date(b.timestamp).getTime();
                return timeB - timeA; // Descending order
              });
              const lastUserMessage = sortedUserMessages[0];
              lastUserTimestamp = lastUserMessage.timestamp instanceof Date
                ? lastUserMessage.timestamp.getTime()
                : new Date(lastUserMessage.timestamp).getTime();
            }
            const apiTimestamp = new Date(response.data.timestamp);
            const apiTimestampMs = apiTimestamp.getTime();

            // Ensure system message comes after user message (at least 1ms after)
            // Always use lastUserTimestamp + 1 to guarantee it comes after the user message
            const systemTimestamp = apiTimestampMs > lastUserTimestamp
              ? new Date(Math.max(apiTimestampMs, lastUserTimestamp + 1))
              : new Date(lastUserTimestamp + 1);

            // Chat completed, create response message with conversation_id verification
            const novaResponse: ChatMessage = {
              id: this.generateId(),
              text: response.data.response.system_message,
              isUser: false,
              timestamp: systemTimestamp,
              conversationId: verifiedConversationId, // Ensure response has conversation_id matching the query
              // Persisted context_id (sc_...) is what download-section-data matches on; message_id (m_...) is only a fallback for older backends
              contextId: response.data.response.context_id || response.data.message_id
            };

            // Build attachments from response.metadata.files and response.metadata.charts
            const meta = response.data.response.metadata;
            const metaAttachments = this.attachmentsFromMetadata(meta);
            if (metaAttachments.length > 0) {
              novaResponse.attachments = metaAttachments;
            }
            // Tables, filters, and SQL from metadata for UI display
            if (meta?.tables?.length) {
              novaResponse.tables = meta.tables;
            }
            if (meta?.filters_applied?.length) {
              novaResponse.filtersApplied = meta.filters_applied;
            }
            if (response.data.response.sql_queries_applied?.length) {
              novaResponse.sqlQueriesApplied = response.data.response.sql_queries_applied;
            }

            // Route the response to the correct conversation - verify conversation_id matches
            // First verify the response conversation_id matches the query conversation_id
            const responseMatchesQuery = verifiedConversationId === conversationId;

            if (!responseMatchesQuery) {
              // console.warn(`Response conversation_id (${verifiedConversationId}) does not match query conversation_id (${conversationId}). Ignoring response.`);
              // Remove from polling set
              this.removePollingConversation(conversationId);
              // Only clear typing if this was for the current conversation
              if (this.currentConversation?.id === conversationId) {
                this.setTyping(false);
              }
              return;
            }

            // Verify response is for the current conversation
            const isForCurrent = this.currentConversation?.id === conversationId && verifiedConversationId === conversationId;
            if (isForCurrent) {
              // Double-check we're still on the same conversation before adding
              // Verify conversation_id one more time to ensure query-response pairing
              if (this.currentConversation?.id === conversationId && novaResponse.conversationId === conversationId) {
                this.addMessage(novaResponse);
              } else {
                // console.warn(`Cannot add response: conversation_id mismatch. Current: ${this.currentConversation?.id}, Response: ${novaResponse.conversationId}`);
              }
            } else {
              // Update the target conversation's messages without affecting the currently viewed messages
              // Only if the conversation_id matches the query
              if (verifiedConversationId === conversationId && novaResponse.conversationId === conversationId) {
                const conversations = [...this._conversations.value];
                const idx = conversations.findIndex(c => c.id === conversationId);
                if (idx !== -1) {
                  const targetConv = conversations[idx];
                  // Verify we're adding to the correct conversation
                  const updatedMessages = [...(targetConv.messages || []), novaResponse];
                  const updatedConv: Conversation = {
                    ...targetConv,
                    messages: updatedMessages,
                    updatedAt: new Date()
                  };
                  conversations[idx] = updatedConv;
                  this._conversations.next(conversations);
                  this.saveConversations();
                }
              }
            }

            // Only clear typing state if this response is for the current conversation
            if (this.currentConversation?.id === conversationId) {
              this.setTyping(false);
            }
            // Remove this conversation from polling set (polling completed)
            this.removePollingConversation(conversationId);

            // Update conversation title locally if provided (from server response, no need to call rename API)
            if (response.data.conversation_title && this.currentConversation?.id === conversationId) {
              this.updateConversationTitleLocally(conversationId, response.data.conversation_title);
            }

            // When async-chat-status succeeds, refresh context from getConversationCustomFilters (single source of truth)
            if (response.success === true) {
              this.novaApiService.getConversationCustomFilters({ conversation_id: conversationId }).subscribe({
                next: (res) => {
                  if (this._currentConversation.value?.id !== conversationId) return;
                  if (!res.success || !res.data?.active_custom_filters) return;
                  const active = res.data.active_custom_filters;
                  const defaultCtx = this.getDefaultContext();
                  const apiContext: NovaConversationContext = {
                    start_date: active.start_date ?? defaultCtx.start_date,
                    end_date: active.end_date ?? defaultCtx.end_date,
                    timeZone: active.timeZone ?? defaultCtx.timeZone,
                    filters: this.apiFiltersToFilterRequests(active.filters) ?? []
                  };
                  this.saveConversationContext(conversationId, apiContext);
                  if (this._currentConversation.value?.id === conversationId) {
                    this.applyConversationContextToUI(apiContext);
                  }
                }
              });
            }

            // Refresh conversations after completion to reflect latest last_updated
            this.refreshConversations();
          } else if ((response.data.status === 'pending' || response.data.status === 'processing') && attempts < maxAttempts) {
            // Still pending/processing, continue polling
            setTimeout(poll, pollInterval);
          } else if (attempts >= maxAttempts) {
            // Timeout
            console.error('Chat polling timeout');
            // Only clear typing if this was for the current conversation
            if (this.currentConversation?.id === conversationId) {
              this.setTyping(false);
            }
            // Remove from polling set
            this.removePollingConversation(conversationId);
          } else {
            // Error or unknown status
            console.error('Chat status error:', response);
            // Only clear typing if this was for the current conversation
            if (this.currentConversation?.id === conversationId) {
              this.setTyping(false);
            }
            // Remove from polling set
            this.removePollingConversation(conversationId);
          }
        },
        error: (error) => {
          console.error('Error checking chat status:', error);
          // Only clear typing if this was for the current conversation
          if (this.currentConversation?.id === conversationId) {
            this.setTyping(false);
          }
          // Remove from polling set
          this.removePollingConversation(conversationId);
        }
      });
    };

    // Start polling
    setTimeout(poll, pollInterval);
  }

  sendSuggestion(suggestion: string): void {
    this.setMessageText(suggestion);
    // Don't automatically send the message - let user modify and send manually
  }



  // File handling
  handleFileUpload(files: FileList): void {
    const maxFileSize = 10 * 1024 * 1024; // 10MB
    const allowedTypes = [
      'text/csv',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    ];

    const newAttachments = [...this.attachments];

    for (let i = 0; i < files.length; i++) {
      const file = files[i];

      if (file.size > maxFileSize) {
        //console.warn(`File ${file.name} is too large. Maximum size is 10MB.`);
        continue;
      }

      if (!allowedTypes.includes(file.type)) {
        //console.warn(`File type ${file.type} is not supported. Only CSV and Excel files are allowed.`);
        continue;
      }

      const attachment: FileAttachment = {
        id: this.generateId(),
        name: file.name,
        size: file.size,
        type: file.type,
        url: URL.createObjectURL(file)
      };
      newAttachments.push(attachment);
    }

    this.setAttachments(newAttachments);
  }

  // Single file upload with 1MB limit
  handleSingleFileUpload(file: File): void {
    const maxFileSize = 1 * 1024 * 1024; // 1MB
    const allowedTypes = [
      'text/csv',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    ];

    // Clear existing attachments first (only one file allowed)
    this.clearAttachments();

    // Validate file size
    if (file.size > maxFileSize) {
      alert(`File "${file.name}" is too large. Maximum size is 1MB.`);
      return;
    }

    // Validate file type
    if (!allowedTypes.includes(file.type)) {
      alert(`File type "${file.type}" is not supported. Only CSV and Excel files are allowed.`);
      return;
    }

    // Upload file to API
    this.novaApiService.uploadFile(file).subscribe({
      next: (attachment: FileAttachment) => {
        this.setAttachments([attachment]);
      },
      error: (error) => {
        console.error('Error uploading file:', error);
        alert('Failed to upload file. Please try again.');
        // Fallback to local file handling
        const fallbackAttachment: FileAttachment = {
          id: this.generateId(),
          name: file.name,
          size: file.size,
          type: file.type,
          url: URL.createObjectURL(file)
        };
        this.setAttachments([fallbackAttachment]);
      }
    });
  }

  // UI state helpers
  shouldShowInitialState(): boolean {
    return this.messages.length === 0;
  }

  shouldShowChatMessages(): boolean {
    return this.messages.length > 0;
  }

  trackByConversationId(index: number, conversation: Conversation): string {
    return conversation.id;
  }

  // History toggle methods
  openNovaHistory(): void {
    this._isHistoryOpen.next(true);
  }

  closeNovaHistory(): void {
    this._isHistoryOpen.next(false);
  }

  toggleNovaHistory(): void {
    this._isHistoryOpen.next(!this._isHistoryOpen.value);
  }

  // Group conversations by date for history display using UTC timestamp
  getConversationGroups(): Array<{title: string, conversations: Conversation[]}> {
    const conversations = this._conversations.value;
    const now = new Date();

    // Get current date in local timezone
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    // Calculate last 7 days (excluding today and yesterday)
    // Last 7 days should be from 2 days ago to 7 days ago
    const last7DaysStart = new Date(today);
    last7DaysStart.setDate(last7DaysStart.getDate() - 7); // 7 days ago from today
    const last7DaysEnd = new Date(today);
    last7DaysEnd.setDate(last7DaysEnd.getDate() - 1); // 2 days ago (start of yesterday)

    // Get current month boundaries
    const thisMonthStart = new Date(now.getFullYear(), now.getMonth(), 1);
    const lastMonthStart = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const lastMonthEnd = new Date(now.getFullYear(), now.getMonth(), 0);

    const groups: Array<{title: string, conversations: Conversation[]}> = [];
    const usedConversationIds = new Set<string>();

    // Today - Current date of the system
    const todayConversations = conversations.filter(conv => {
      const convDate = new Date(conv.timestamp);
      const convDateOnly = new Date(convDate.getFullYear(), convDate.getMonth(), convDate.getDate());
      return convDateOnly.getTime() === today.getTime();
    });
    if (todayConversations.length > 0) {
      groups.push({ title: 'Today', conversations: todayConversations });
      todayConversations.forEach(conv => usedConversationIds.add(conv.id));
    }

    // Yesterday - Yesterday date of system considering current date
    const yesterdayConversations = conversations.filter(conv => {
      const convDate = new Date(conv.timestamp);
      const convDateOnly = new Date(convDate.getFullYear(), convDate.getMonth(), convDate.getDate());
      return convDateOnly.getTime() === yesterday.getTime();
    });
    if (yesterdayConversations.length > 0) {
      groups.push({ title: 'Yesterday', conversations: yesterdayConversations });
      yesterdayConversations.forEach(conv => usedConversationIds.add(conv.id));
    }

    // Last 7 days - conversations from last 7 days (excluding today and yesterday)
    const last7DaysConversations = conversations.filter(conv => {
      const convDate = new Date(conv.timestamp);
      const convDateOnly = new Date(convDate.getFullYear(), convDate.getMonth(), convDate.getDate());
      return convDateOnly >= last7DaysStart && convDateOnly < last7DaysEnd && !usedConversationIds.has(conv.id);
    });
    if (last7DaysConversations.length > 0) {
      groups.push({ title: 'Last 7 days', conversations: last7DaysConversations });
      last7DaysConversations.forEach(conv => usedConversationIds.add(conv.id));
    }

    // This month - current month (excluding today, yesterday, and last 7 days)
    const thisMonthConversations = conversations.filter(conv => {
      const convDate = new Date(conv.timestamp);
      return convDate >= thisMonthStart && convDate < today && !usedConversationIds.has(conv.id);
    });
    if (thisMonthConversations.length > 0) {
      groups.push({ title: 'This month', conversations: thisMonthConversations });
      thisMonthConversations.forEach(conv => usedConversationIds.add(conv.id));
    }

    // Last month - last month (excluding already used conversations)
    const lastMonthConversations = conversations.filter(conv => {
      const convDate = new Date(conv.timestamp);
      return convDate >= lastMonthStart && convDate < lastMonthEnd && !usedConversationIds.has(conv.id);
    });
    if (lastMonthConversations.length > 0) {
      groups.push({ title: 'Last month', conversations: lastMonthConversations });
      lastMonthConversations.forEach(conv => usedConversationIds.add(conv.id));
    }

    // Older - remaining conversations (before last month, excluding already used conversations)
    const olderConversations = conversations.filter(conv => {
      const convDate = new Date(conv.timestamp);
      return convDate < lastMonthStart && !usedConversationIds.has(conv.id);
    });
    if (olderConversations.length > 0) {
      groups.push({ title: 'Older', conversations: olderConversations });
    }

    return groups;
  }


  /**
   * Build FileAttachment[] from API response metadata (files + charts).
   * Used by async-chat-status and get-conversation-messages responses.
   */
  private attachmentsFromMetadata(metadata: ResponseMetadata | undefined): FileAttachment[] {
    if (!metadata) return [];
    const attachments: FileAttachment[] = [];
    const files = metadata.files || [];
    const charts = metadata.charts || [];
    files.forEach(f => {
      attachments.push({
        id: f.file_id,
        name: f.file_name || f.file_title || this.getFileNameFromUrl(f.file_url, f.file_id),
        size: 0,
        type: this.getFileTypeFromUrl(f.file_url),
        url: f.file_url,
        response_type: f.file_type || 'file',
        step: f.step
      });
    });
    charts.forEach(c => {
      attachments.push({
        id: c.file_id,
        name: c.chart_title || this.getFileNameFromUrl(c.file_url, c.file_id),
        size: 0,
        type: this.getFileTypeFromUrl(c.file_url),
        url: c.file_url,
        response_type: c.file_type || 'chart',
        chartDescription: c.chart_description,
        thumbnail_url: c.thumbnail_url,
        step: c.step
      });
    });
    return attachments;
  }

  // Helper methods for file handling
  private getFileNameFromUrl(fileUrl: string, fileId: string): string {
    try {
      // Extract filename from URL path
      const url = new URL(fileUrl);
      const pathname = url.pathname;
      const filename = pathname.split('/').pop();

      if (filename && filename.includes('.')) {
        return filename;
      }

      // If no filename in URL, create one using fileId and file extension (e.g. "pdf"), not MIME type
      const extension = this.getFileExtensionFromUrl(fileUrl);
      return `file_${fileId}.${extension}`;
    } catch (error) {
      console.error('Error parsing file URL:', error);
      return `file_${fileId}`;
    }
  }

  /** Returns file extension from URL path (e.g. "pdf", "csv") for use in filenames. */
  private getFileExtensionFromUrl(fileUrl: string): string {
    try {
      const url = new URL(fileUrl);
      const pathname = url.pathname;
      const segment = pathname.split('.').pop()?.toLowerCase();
      const knownExtensions = ['pdf', 'csv', 'xlsx', 'xls', 'png', 'jpg', 'jpeg', 'gif', 'svg', 'webp', 'html', 'htm', 'gz'];
      if (segment && knownExtensions.includes(segment)) {
        return segment;
      }
      return 'bin';
    } catch {
      return 'bin';
    }
  }

  private getFileTypeFromUrl(fileUrl: string): string {
    try {
      const url = new URL(fileUrl);
      const pathname = url.pathname;
      const extension = pathname.split('.').pop()?.toLowerCase();

      // Map common extensions to MIME types
      const extensionMap: { [key: string]: string } = {
        'pdf': 'application/pdf',
        'csv': 'text/csv',
        'xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'xls': 'application/vnd.ms-excel',
        'png': 'image/png',
        'jpg': 'image/jpeg',
        'jpeg': 'image/jpeg',
        'gif': 'image/gif',
        'svg': 'image/svg+xml',
        'webp': 'image/webp',
        'html': 'text/html',
        'htm': 'text/html',
        'gz': 'application/gzip'
      };

      return extensionMap[extension || ''] || 'application/octet-stream';
    } catch (error) {
      console.error('Error parsing file type from URL:', error);
      return 'application/octet-stream';
    }
  }
}
